import { c as _c } from "react/compiler-runtime";
import { AccountLockupFragment, useChains } from "@opensea/app-kit";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { ENS_WRAPPER_ADDRESS } from "@/constants/constants";
import { ExpandedSearch_accountFragment, ExpandedSearch_collectionFragment, ExpandedSearch_currencyFragment, ExpandedSearch_itemFragment } from "./ExpandedSearch.fragment";
import { useRecentSearches } from "./recent-searches/hooks";
import { mapAccountToSearchResultItem } from "./results/ExpandedSearchAccountResult";
import { mapCollectionToSearchResultItem } from "./results/ExpandedSearchCollectionResult";
import { mapCurrencyToSearchResultItem } from "./results/ExpandedSearchCurrencyResult";
import { mapItemToSearchResultItem } from "./results/ExpandedSearchItemResult";
import { mapRecentSearchToSearchResultItem } from "./results/ExpandedSearchRecentResult";
import { combineSearchResults, getSectionsFromQuery } from "./utils";
export const EXPANDED_SEARCH_ACCOUNTS_LIMIT = 3;
export const EXPANDED_SEARCH_ACCOUNTS_ENHANCED_LIMIT = 12;
const accountsQuery = graphql(`
    query ExpandedSearchQueryAccount($query: String!, $limit: Int!) {
      accountsByQuery(query: $query, limit: $limit) {
        ...ExpandedSearchAccount
      }
    }
  `, [ExpandedSearch_accountFragment]);
export const EXPANDED_SEARCH_COLLECTIONS_LIMIT = 5;
export const EXPANDED_SEARCH_COLLECTIONS_ENHANCED_LIMIT = 12;
const collectionsQuery = graphql(`
    query ExpandedSearchQueryCollection(
      $query: String!
      $limit: Int!
      $chains: [ChainIdentifier!]
    ) {
      collectionsByQuery(query: $query, limit: $limit, chains: $chains) {
        ...ExpandedSearchCollection
      }
    }
  `, [ExpandedSearch_collectionFragment]);
export const EXPANDED_SEARCH_ITEMS_LIMIT = 3;
export const EXPANDED_SEARCH_ITEMS_ENHANCED_LIMIT = 12;
export const EXPANDED_SEARCH_ITEMS_ENS_LIMIT = 10;
const itemsQuery = graphql(`
    query ExpandedSearchQueryItems(
      $query: String!
      $limit: Int!
      $chains: [ChainIdentifier!]
    ) {
      itemsByQuery(query: $query, limit: $limit, chains: $chains) {
        ...ExpandedSearchItem
      }
    }
  `, [ExpandedSearch_itemFragment]);
export const EXPANDED_SEARCH_CURRENCIES_LIMIT = 5;
export const EXPANDED_SEARCH_CURRENCIES_ENHANCED_LIMIT = 15;
const currenciesQuery = graphql(`
    query ExpandedSearchQueryCurrencies(
      $query: String!
      $limit: Int!
      $chain: ChainIdentifier
    ) {
      currenciesByQuery(
        query: $query
        limit: $limit
        allowlistOnly: false
        chain: $chain
      ) {
        ...ExpandedSearchCurrency
      }
    }
  `, [ExpandedSearch_currencyFragment]);
const queryOptions = {
  throwOnError: false
};
export function useExpandedSearchQueryResults(t0) {
  const $ = _c(71);
  const {
    query,
    debouncedQuery,
    selectedCategory: t1,
    selectedChain: t2
  } = t0;
  const selectedCategory = t1 === undefined ? "all" : t1;
  const selectedChain = t2 === undefined ? "all" : t2;
  const {
    isChainCurrencyOnly
  } = useChains();
  const pause = debouncedQuery.trim().length === 0;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      suspense: false
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const stableContext = t3;
  const t4 = selectedCategory === "collections" ? EXPANDED_SEARCH_COLLECTIONS_ENHANCED_LIMIT : selectedCategory === "all" ? EXPANDED_SEARCH_COLLECTIONS_LIMIT : 0;
  let t5;
  if ($[1] !== selectedChain) {
    t5 = selectedChain && selectedChain !== "all" ? {
      chains: [selectedChain]
    } : {};
    $[1] = selectedChain;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== debouncedQuery || $[4] !== t4 || $[5] !== t5) {
    t6 = {
      query: debouncedQuery,
      limit: t4,
      ...t5
    };
    $[3] = debouncedQuery;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isChainCurrencyOnly || $[8] !== pause || $[9] !== selectedCategory || $[10] !== selectedChain) {
    t7 = pause || selectedCategory !== "all" && selectedCategory !== "collections" || selectedChain !== "all" && isChainCurrencyOnly(selectedChain);
    $[7] = isChainCurrencyOnly;
    $[8] = pause;
    $[9] = selectedCategory;
    $[10] = selectedChain;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      query: collectionsQuery,
      variables: t6,
      pause: t7,
      context: stableContext
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [t9] = useQuery(t8, queryOptions);
  const {
    data: queryCollectionsData,
    error: queryCollectionsError,
    fetching: isCollectionsLoading
  } = t9;
  let t10;
  if ($[15] !== queryCollectionsData?.collectionsByQuery) {
    t10 = queryCollectionsData?.collectionsByQuery || [];
    $[15] = queryCollectionsData?.collectionsByQuery;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const queryCollections = t10;
  let limit = 0;
  if (debouncedQuery.toLowerCase().endsWith(".eth")) {
    limit = EXPANDED_SEARCH_ITEMS_ENS_LIMIT;
  } else {
    if (selectedCategory === "items") {
      limit = EXPANDED_SEARCH_ITEMS_ENHANCED_LIMIT;
    } else {
      if (selectedCategory === "all") {
        limit = EXPANDED_SEARCH_ITEMS_LIMIT;
      }
    }
  }
  let t11;
  if ($[17] !== selectedChain) {
    t11 = selectedChain && selectedChain !== "all" ? {
      chains: [selectedChain]
    } : {};
    $[17] = selectedChain;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== debouncedQuery || $[20] !== limit || $[21] !== t11) {
    t12 = {
      query: debouncedQuery,
      limit,
      ...t11
    };
    $[19] = debouncedQuery;
    $[20] = limit;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isChainCurrencyOnly || $[24] !== pause || $[25] !== selectedCategory || $[26] !== selectedChain) {
    t13 = pause || selectedCategory !== "all" && selectedCategory !== "items" || selectedChain !== "all" && isChainCurrencyOnly(selectedChain);
    $[23] = isChainCurrencyOnly;
    $[24] = pause;
    $[25] = selectedCategory;
    $[26] = selectedChain;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = {
      query: itemsQuery,
      variables: t12,
      pause: t13,
      context: stableContext
    };
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const [t15] = useQuery(t14, queryOptions);
  const {
    data: queryItemsData,
    error: queryItemsError,
    fetching: isItemsLoading
  } = t15;
  let t16;
  if ($[31] !== queryItemsData?.itemsByQuery) {
    t16 = queryItemsData?.itemsByQuery || [];
    $[31] = queryItemsData?.itemsByQuery;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const queryItems = t16.filter(_temp);
  const t17 = selectedCategory === "wallets" ? EXPANDED_SEARCH_ACCOUNTS_ENHANCED_LIMIT : selectedCategory === "all" ? EXPANDED_SEARCH_ACCOUNTS_LIMIT : 0;
  let t18;
  if ($[33] !== debouncedQuery || $[34] !== t17) {
    t18 = {
      query: debouncedQuery,
      limit: t17
    };
    $[33] = debouncedQuery;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = pause || selectedCategory !== "all" && selectedCategory !== "wallets";
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = {
      query: accountsQuery,
      variables: t18,
      pause: t19,
      context: stableContext
    };
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const [t21] = useQuery(t20, queryOptions);
  const {
    data: queryAccountsData,
    error: queryAccountsError,
    fetching: isAccountsLoading
  } = t21;
  let t22;
  if ($[39] !== queryAccountsData?.accountsByQuery) {
    t22 = queryAccountsData?.accountsByQuery || [];
    $[39] = queryAccountsData?.accountsByQuery;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const queryAccounts = t22;
  const t23 = selectedCategory === "tokens" ? EXPANDED_SEARCH_CURRENCIES_ENHANCED_LIMIT : selectedCategory === "all" ? EXPANDED_SEARCH_CURRENCIES_LIMIT : 0;
  let t24;
  if ($[41] !== selectedChain) {
    t24 = selectedChain && selectedChain !== "all" ? {
      chain: selectedChain
    } : {};
    $[41] = selectedChain;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== debouncedQuery || $[44] !== t23 || $[45] !== t24) {
    t25 = {
      query: debouncedQuery,
      limit: t23,
      ...t24
    };
    $[43] = debouncedQuery;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  const t26 = pause || selectedCategory !== "all" && selectedCategory !== "tokens";
  let t27;
  if ($[47] !== t25 || $[48] !== t26) {
    t27 = {
      query: currenciesQuery,
      variables: t25,
      pause: t26,
      context: stableContext
    };
    $[47] = t25;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  const [t28] = useQuery(t27, queryOptions);
  const {
    data: queryCurrenciesData,
    error: queryCurrenciesError,
    fetching: isCurrenciesLoading
  } = t28;
  let t29;
  if ($[50] !== queryCurrenciesData?.currenciesByQuery) {
    t29 = queryCurrenciesData?.currenciesByQuery || [];
    $[50] = queryCurrenciesData?.currenciesByQuery;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const queryCurrencies = t29;
  const recentSearches = useRecentSearches();
  const sections = getSectionsFromQuery(query);
  const searchResults = combineSearchResults({
    collections: queryCollections.map(mapCollectionToSearchResultItem),
    currencies: queryCurrencies.map(mapCurrencyToSearchResultItem),
    items: queryItems.map(mapItemToSearchResultItem),
    accounts: queryAccounts.map(mapAccountToSearchResultItem)
  }, sections);
  let t30;
  if ($[52] !== queryAccountsError || $[53] !== queryCollectionsError || $[54] !== queryCurrenciesError || $[55] !== queryItemsError) {
    t30 = [queryCollectionsError, queryAccountsError, queryItemsError, queryCurrenciesError].filter(_temp2);
    $[52] = queryAccountsError;
    $[53] = queryCollectionsError;
    $[54] = queryCurrenciesError;
    $[55] = queryItemsError;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  const errors = t30;
  const fetching = isAccountsLoading || isCollectionsLoading || isItemsLoading || isCurrenciesLoading;
  let t31;
  if ($[57] !== errors || $[58] !== fetching || $[59] !== isAccountsLoading || $[60] !== isCollectionsLoading || $[61] !== isCurrenciesLoading || $[62] !== isItemsLoading || $[63] !== queryAccounts || $[64] !== queryCollections || $[65] !== queryCurrencies || $[66] !== queryItems || $[67] !== recentSearches || $[68] !== searchResults || $[69] !== sections) {
    t31 = {
      searchResults,
      sections,
      accounts: queryAccounts,
      isAccountsLoading,
      collections: queryCollections,
      isCollectionsLoading,
      items: queryItems,
      isItemsLoading,
      currencies: queryCurrencies,
      isCurrenciesLoading,
      recentSearches,
      errors,
      fetching
    };
    $[57] = errors;
    $[58] = fetching;
    $[59] = isAccountsLoading;
    $[60] = isCollectionsLoading;
    $[61] = isCurrenciesLoading;
    $[62] = isItemsLoading;
    $[63] = queryAccounts;
    $[64] = queryCollections;
    $[65] = queryCurrencies;
    $[66] = queryItems;
    $[67] = recentSearches;
    $[68] = searchResults;
    $[69] = sections;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  return t31;
}
function _temp2(e) {
  return Boolean(e);
}
function _temp(item) {
  const itemData = readFragment(ExpandedSearch_itemFragment, item);
  const ownerData = readFragment(AccountLockupFragment, itemData.owner);
  return ownerData?.address !== ENS_WRAPPER_ADDRESS;
}
export const EXPANDED_SEARCH_TRENDING_COLLECTIONS_LIMIT = 6;
export const EXPANDED_SEARCH_TRENDING_COLLECTIONS_ENHANCED_LIMIT = 20;
const topCollectionsQuery = graphql(`
    query ExpandedSearchTopCollectionsItemsQuery(
      $limit: Int!
      $filter: TopCollectionsFilter!
      $sort: TopCollectionsSort!
    ) {
      topCollections(limit: $limit, filter: $filter, sort: $sort) {
        items {
          ...ExpandedSearchCollection
        }
      }
    }
  `, [ExpandedSearch_collectionFragment]);
export const EXPANDED_SEARCH_TRENDING_CURRENCIES_FETCH_LIMIT = 25;
export const EXPANDED_SEARCH_TRENDING_CURRENCIES_DISPLAY_LIMIT = 20;
export const EXPANDED_SEARCH_TRENDING_CURRENCIES_DISPLAY_LIMIT_ALL = 6;
const trendingCurrenciesQuery = graphql(`
    query ExpandedSearchTrendingCurrenciesQuery(
      $filter: CurrenciesFilter!
      $limit: Int!
      $sort: CurrenciesSort!
    ) {
      topCurrencies(filter: $filter, limit: $limit, sort: $sort) {
        items {
          imageUrl
          ...ExpandedSearchCurrency
        }
      }
    }
  `, [ExpandedSearch_currencyFragment]);
const OPERATION_CONTEXT = {
  suspense: false
};
export function useExpandedSearchTrendingCollections(t0) {
  const $ = _c(33);
  const {
    pause,
    selectedCategory: t1,
    selectedChain: t2,
    isVerified
  } = t0;
  const selectedCategory = t1 === undefined ? "all" : t1;
  const selectedChain = t2 === undefined ? "all" : t2;
  const {
    isChainCurrencyOnly,
    svmChains
  } = useChains();
  const limit = selectedCategory === "collections" ? EXPANDED_SEARCH_TRENDING_COLLECTIONS_ENHANCED_LIMIT : EXPANDED_SEARCH_TRENDING_COLLECTIONS_LIMIT;
  let t3;
  if ($[0] !== isChainCurrencyOnly || $[1] !== pause || $[2] !== selectedCategory || $[3] !== selectedChain) {
    t3 = pause || selectedCategory !== "all" && selectedCategory !== "collections" || selectedChain !== "all" && isChainCurrencyOnly(selectedChain);
    $[0] = isChainCurrencyOnly;
    $[1] = pause;
    $[2] = selectedCategory;
    $[3] = selectedChain;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const basePause = t3;
  let t4;
  if ($[5] !== selectedChain) {
    t4 = selectedChain && selectedChain !== "all" ? {
      chain: selectedChain
    } : {};
    $[5] = selectedChain;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = isVerified || undefined;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      ...t4,
      isVerified: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      by: "ONE_DAY_VOLUME",
      direction: "DESC"
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== limit || $[12] !== t6) {
    t8 = {
      limit,
      filter: t6,
      sort: t7
    };
    $[11] = limit;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== basePause || $[15] !== t8) {
    t9 = {
      query: topCollectionsQuery,
      context: OPERATION_CONTEXT,
      pause: basePause,
      variables: t8
    };
    $[14] = basePause;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const [t10] = useQuery(t9, queryOptions);
  const {
    data: oneDayData,
    fetching: oneDayFetching
  } = t10;
  let t11;
  if ($[17] !== oneDayData?.topCollections.items) {
    t11 = oneDayData?.topCollections.items ?? [];
    $[17] = oneDayData?.topCollections.items;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const oneDayCollections = t11;
  const shouldUseFallback = !oneDayFetching && oneDayCollections.length === 0;
  const t12 = basePause || !shouldUseFallback;
  let t13;
  if ($[19] !== selectedChain) {
    t13 = selectedChain && selectedChain !== "all" ? {
      chain: selectedChain
    } : {};
    $[19] = selectedChain;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = isVerified || undefined;
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = {
      ...t13,
      isVerified: t14
    };
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      by: "THIRTY_DAYS_VOLUME",
      direction: "DESC"
    };
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== limit || $[26] !== t15) {
    t17 = {
      limit,
      filter: t15,
      sort: t16
    };
    $[25] = limit;
    $[26] = t15;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t12 || $[29] !== t17) {
    t18 = {
      query: topCollectionsQuery,
      context: OPERATION_CONTEXT,
      pause: t12,
      variables: t17
    };
    $[28] = t12;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const [t19] = useQuery(t18, queryOptions);
  const {
    data: thirtyDayData
  } = t19;
  const isCurrencyOnly = isChainCurrencyOnly(selectedChain);
  const isSvmChain = svmChains.some(chain => chain.identifier === selectedChain);
  if (selectedChain !== "all" && (isCurrencyOnly || isSvmChain)) {
    return;
  }
  if (oneDayCollections.length > 0) {
    return oneDayCollections;
  }
  if (shouldUseFallback) {
    let t20;
    if ($[31] !== thirtyDayData?.topCollections.items) {
      t20 = thirtyDayData?.topCollections.items ?? [];
      $[31] = thirtyDayData?.topCollections.items;
      $[32] = t20;
    } else {
      t20 = $[32];
    }
    const thirtyDayCollections = t20;
    return thirtyDayCollections;
  }
  return oneDayCollections;
}
export function useExpandedSearchTopCurrencies(t0) {
  const $ = _c(13);
  const {
    pause,
    selectedCategory: t1,
    selectedChain: t2
  } = t0;
  const selectedCategory = t1 === undefined ? "all" : t1;
  const selectedChain = t2 === undefined ? "all" : t2;
  const displayLimit = selectedCategory === "tokens" ? EXPANDED_SEARCH_TRENDING_CURRENCIES_DISPLAY_LIMIT : EXPANDED_SEARCH_TRENDING_CURRENCIES_DISPLAY_LIMIT_ALL;
  const t3 = pause || selectedCategory !== "all" && selectedCategory !== "tokens";
  let t4;
  if ($[0] !== selectedChain) {
    t4 = {
      swappable: true,
      ...(selectedChain && selectedChain !== "all" ? {
        chains: [selectedChain]
      } : {})
    };
    $[0] = selectedChain;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      by: "ONE_DAY_PRICE_CHANGE",
      direction: "DESC"
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t4) {
    t6 = {
      filter: t4,
      limit: EXPANDED_SEARCH_TRENDING_CURRENCIES_FETCH_LIMIT,
      sort: t5
    };
    $[3] = t4;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t3 || $[6] !== t6) {
    t7 = {
      query: trendingCurrenciesQuery,
      context: OPERATION_CONTEXT,
      pause: t3,
      variables: t6
    };
    $[5] = t3;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const [t8] = useQuery(t7, queryOptions);
  const {
    data: trendingCurrenciesData
  } = t8;
  let t9;
  if ($[8] !== trendingCurrenciesData?.topCurrencies.items) {
    t9 = trendingCurrenciesData?.topCurrencies.items ?? [];
    $[8] = trendingCurrenciesData?.topCurrencies.items;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const trendingCurrencies = t9;
  let t10;
  if ($[10] !== displayLimit || $[11] !== trendingCurrencies) {
    t10 = trendingCurrencies.filter(_temp3).slice(0, displayLimit);
    $[10] = displayLimit;
    $[11] = trendingCurrencies;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  return t10;
}
function _temp3(currency) {
  return currency.imageUrl;
}
export function useExpandedSearchVisibleItems({
  searchResults,
  isQueryNotEmpty,
  recentSearches = [],
  trendingCollections = [],
  topCurrencies = [],
  selectedCategory = "all",
  selectedChain = "all"
}) {
  // Chain filtering for recent searches is NECESSARY (not redundant) because:
  // - Recent searches are local storage data spanning all chains
  // - GraphQL queries only filter search results, not historical recent searches
  // Memoize expensive chain filtering to avoid readFragment calls on every render
  function getFilteredRecentSearches() {
    if (selectedChain === "all") {
      return recentSearches;
    }
    return recentSearches.filter(recentSearch => {
      if (recentSearch.type === "currency") {
        const currency = readFragment(ExpandedSearch_currencyFragment, recentSearch.data);
        return currency.chain?.identifier === selectedChain;
      }
      if (recentSearch.type === "item") {
        const item = readFragment(ExpandedSearch_itemFragment, recentSearch.data);
        return item.collection?.chain?.identifier === selectedChain;
      }
      if (recentSearch.type === "collection") {
        const collection = readFragment(ExpandedSearch_collectionFragment, recentSearch.data);
        return collection.chain?.identifier === selectedChain;
      }
      // For query and account types, don't filter by chain
      return true;
    });
  }
  const filteredRecentSearches = getFilteredRecentSearches();

  // Memoize category filtering for recent searches to avoid redundant work
  function getCategoryFilteredRecentSearches() {
    switch (selectedCategory) {
      case "collections":
        return filteredRecentSearches.filter(recentSearch => recentSearch.type === "collection");
      case "items":
        return filteredRecentSearches.filter(recentSearch => recentSearch.type === "item");
      case "tokens":
        return filteredRecentSearches.filter(recentSearch => recentSearch.type === "currency");
      case "wallets":
        return filteredRecentSearches.filter(recentSearch => recentSearch.type === "account");
      default:
        return filteredRecentSearches;
    }
  }
  const categoryFilteredRecentSearches = getCategoryFilteredRecentSearches();
  function getVisibleItems() {
    if (isQueryNotEmpty) {
      return (searchResults || []).filter(item => {
        switch (selectedCategory) {
          case "collections":
            return item.type === "collection";
          case "items":
            return item.type === "item";
          case "tokens":
            return item.type === "currency";
          case "wallets":
            return item.type === "account";
          default:
            return true;
        }
      });
    }
    const items = [];

    // Add recent searches based on category
    items.push(...categoryFilteredRecentSearches.map(mapRecentSearchToSearchResultItem));

    // Add trending collections for "all" and "collections" categories
    if (selectedCategory === "all" || selectedCategory === "collections") {
      items.push(...trendingCollections.map(mapCollectionToSearchResultItem));
    }

    // Add top currencies for "all" and "tokens" categories
    if (selectedCategory === "all" || selectedCategory === "tokens") {
      items.push(...topCurrencies.map(mapCurrencyToSearchResultItem));
    }
    return items;
  }
  const visibleItems = getVisibleItems();
  return {
    visibleItems
  };
}